<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { FlowLayout } from '@Shared';
import QrDisplay from './QrDisplay.vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { getHostRouter } from '@Src/mfe.config';

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();

const qrCodeUrl = ref<string>('');

const getCompanySlug = (): string | null => {
    const hostRouter = getHostRouter();
    const slug = hostRouter?.currentRoute?.value?.params?.companySlug as string | undefined;
    if (slug) return slug;

    // Fallback: host company store persisted to sessionStorage
    try {
        const persisted = JSON.parse(sessionStorage.getItem('company') || '{}');
        if (persisted.companySlug) return persisted.companySlug;
    } catch { /* ignore */ }

    return null;
};

const ensureCompanySlugInPath = (url: URL): void => {
    const slug = getCompanySlug();
    if (slug && !url.pathname.startsWith(`/${slug}/`)) {
        url.pathname = `/${slug}${url.pathname}`;
    }
};

const buildLegacyQrCodeUrl = (): string => {
    const currentUrl = new URL(globalThis.location.href);
    ensureCompanySlugInPath(currentUrl);
    const params = new URLSearchParams(currentUrl.search);
    params.set('utm_source', 'qrcode');
    params.set('flow', 'IdvMobileAuthFlow');
    currentUrl.search = params.toString();
    return currentUrl.href;
};

const buildUrlWithTemporarySessionId = (temporarySessionId: string): string => {
    const currentUrl = new URL(globalThis.location.href);
    ensureCompanySlugInPath(currentUrl);
    const params = new URLSearchParams(currentUrl.search);
    params.delete('clientId');
    params.set('temporarySessionId', temporarySessionId);
    params.set('utm_source', 'qrcode');
    params.set('flow', 'IdvMobileAuthFlow');
    currentUrl.search = params.toString();
    return currentUrl.href;
};

onMounted(() => {
    // If temporarySessionId is already in the URL (injected by router guard),
    // use it directly — no need to call GetMobileSession again
    const currentUrl = new URL(globalThis.location.href);
    const temporarySessionId = currentUrl.searchParams.get('temporarySessionId');

    if (temporarySessionId) {
        qrCodeUrl.value = buildUrlWithTemporarySessionId(temporarySessionId);
    } else {
        qrCodeUrl.value = buildLegacyQrCodeUrl();
    }
});

</script>


<template>
    <flow-layout
        suppress-back-to-dashboard
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">
            <app-form-content-layout>
                <h1>Verify your identity</h1>
                <p>
                    <strong>{{ companyStore.company?.name }}</strong> is using CertifID to verify your identity.
                </p>
                <p>
                    To continue, scan this QR code with your phone camera.
                </p>
                <div v-if="qrCodeUrl" class="qr-display-wrapper">
                    <qr-display :url="qrCodeUrl" />
                </div>
            </app-form-content-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
.qr-display-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1rem;
}
</style>